/**
 * Hangman: the word list, the round's rules and the gallows.
 *
 * A round is a plain value and every move returns a new one, so the rules can be tested
 * without a page. Letters are compared in upper case after NFC normalisation, so a word
 * list in Spanish, French or German works: the keyboard is A–Z plus whatever other
 * letters the list uses, and an accented letter is its own key.
 */

import { type Rng, randInt, shuffled } from "@/lib/tool-kit/worksheets/random";

export const MIN_MISSES = 4;
export const MAX_MISSES = 10;
export const DEFAULT_MISSES = 6;
export const MAX_WORDS = 200;
export const MAX_ANSWER_LETTERS = 40;
/** The figure is drawn in this many strokes: head, body, two arms, two legs. */
export const FIGURE_PARTS = 6;

const LETTER = /\p{L}/u;
const LINE_BREAK = /\r?\n/;
const SEPARATOR = /\t|\s*\|\s*|\s*:\s*|\s*=\s*|\s*[–—]\s*|\s+-\s+/;
const BASIC_LETTERS = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

export interface HangmanEntry {
  /** As typed, trimmed. */
  answer: string;
  clue: string;
  /** 1-based line in the input. */
  line: number;
}

export type RejectedWhy = "no-letters" | "too-long";

export interface ParsedHangman {
  entries: HangmanEntry[];
  rejected: { line: number; why: RejectedWhy }[];
  /** Repeated answers, each used once. */
  duplicates: number;
  /** Lines beyond `MAX_WORDS`, ignored. */
  overflow: number;
}

export function isLetter(char: string): boolean {
  return LETTER.test(char);
}

/** The answer as played: NFC, upper case. */
export function normalizeAnswer(answer: string, locale?: string): string {
  return answer.normalize("NFC").toLocaleUpperCase(locale);
}

function letterCount(answer: string): number {
  return [...answer].filter(isLetter).length;
}

/**
 * Read the list: one answer per line, an optional clue after a separator (a tab, `|`,
 * a colon, `=`, a dash or a spaced hyphen — a bare hyphen stays inside the answer, so
 * "well-being" works).
 */
export function parseHangmanList(text: string, locale?: string): ParsedHangman {
  const entries: HangmanEntry[] = [];
  const rejected: ParsedHangman["rejected"] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  let overflow = 0;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const line = raw.trim();
    if (line.length === 0) {
      continue;
    }
    const match = SEPARATOR.exec(line);
    const answer = (match ? line.slice(0, match.index) : line).trim();
    const clue = match ? line.slice(match.index + match[0].length).trim() : "";
    const letters = letterCount(answer);
    if (letters === 0) {
      rejected.push({ line: index + 1, why: "no-letters" });
    } else if (letters > MAX_ANSWER_LETTERS) {
      rejected.push({ line: index + 1, why: "too-long" });
    } else if (seen.has(normalizeAnswer(answer, locale))) {
      duplicates += 1;
    } else if (entries.length >= MAX_WORDS) {
      overflow += 1;
    } else {
      seen.add(normalizeAnswer(answer, locale));
      entries.push({ answer, clue, line: index + 1 });
    }
  }
  return { entries, rejected, duplicates, overflow };
}

/** The keys: A–Z, then any other letter the answers use, in order. */
export function keyboardFor(
  entries: readonly HangmanEntry[],
  locale?: string
): string[] {
  const extra = new Set<string>();
  for (const entry of entries) {
    for (const char of normalizeAnswer(entry.answer, locale)) {
      if (isLetter(char) && !BASIC_LETTERS.includes(char)) {
        extra.add(char);
      }
    }
  }
  return [
    ...BASIC_LETTERS,
    ...[...extra].sort((a, b) => a.localeCompare(b, locale)),
  ];
}

/** A shuffled order to play the entries in. */
export function dealOrder(count: number, rng: Rng): number[] {
  return shuffled(
    rng,
    Array.from({ length: count }, (_, i) => i)
  );
}

export type RoundStatus = "playing" | "won" | "lost";

export interface Round {
  /** Upper-cased answer. */
  answer: string;
  clue: string;
  guessed: string[];
  misses: number;
  maxMisses: number;
  status: RoundStatus;
  hintShown: boolean;
}

export function startRound(
  entry: HangmanEntry,
  maxMisses: number,
  locale?: string
): Round {
  const clamped = Math.min(
    MAX_MISSES,
    Math.max(MIN_MISSES, Math.round(maxMisses))
  );
  return {
    answer: normalizeAnswer(entry.answer, locale),
    clue: entry.clue,
    guessed: [],
    misses: 0,
    maxMisses: clamped,
    status: "playing",
    hintShown: false,
  };
}

/** The distinct letters still to find. */
export function lettersToFind(round: Round): string[] {
  return [...new Set([...round.answer].filter(isLetter))].filter(
    (char) => !round.guessed.includes(char)
  );
}

function settle(round: Round): Round {
  if (lettersToFind(round).length === 0) {
    return { ...round, status: "won" };
  }
  if (round.misses >= round.maxMisses) {
    return { ...round, status: "lost" };
  }
  return round;
}

export interface GuessResult {
  round: Round;
  /** How many times the letter occurs in the answer; 0 for a miss. */
  found: number;
  /** False when the guess did nothing: repeated, or the round is over. */
  counted: boolean;
}

/** Guess one letter. A repeated letter, or a guess after the round ends, changes nothing. */
export function guess(
  round: Round,
  letter: string,
  locale?: string
): GuessResult {
  const key = normalizeAnswer(letter, locale);
  if (
    round.status !== "playing" ||
    round.guessed.includes(key) ||
    !isLetter(key)
  ) {
    return { round, found: 0, counted: false };
  }
  const found = [...round.answer].filter((char) => char === key).length;
  const next = {
    ...round,
    guessed: [...round.guessed, key],
    misses: round.misses + (found === 0 ? 1 : 0),
  };
  return { round: settle(next), found, counted: true };
}

/** Spend a miss to reveal one unguessed letter, if that would not end the round. */
export function revealLetter(round: Round, rng: Rng): GuessResult {
  const open = lettersToFind(round);
  if (
    round.status !== "playing" ||
    open.length <= 1 ||
    round.misses + 1 >= round.maxMisses
  ) {
    return { round, found: 0, counted: false };
  }
  const letter = open[randInt(rng, 0, open.length - 1)] as string;
  const found = [...round.answer].filter((char) => char === letter).length;
  const next = {
    ...round,
    guessed: [...round.guessed, letter],
    misses: round.misses + 1,
  };
  return { round: settle(next), found, counted: true };
}

/** One slot of the displayed answer. */
export interface Slot {
  char: string;
  /** Letters show when guessed (or when the round is over); other characters always show. */
  shown: boolean;
  isLetter: boolean;
}

export function slots(round: Round): Slot[] {
  const reveal = round.status !== "playing";
  return [...round.answer].map((char) => {
    const letter = isLetter(char);
    return {
      char,
      isLetter: letter,
      shown: !letter || reveal || round.guessed.includes(char),
    };
  });
}

/** Letters guessed that are not in the answer. */
export function wrongLetters(round: Round): string[] {
  return round.guessed.filter((char) => !round.answer.includes(char));
}

/** How many of the figure's six strokes are drawn after `misses` of `max` misses. */
export function figureParts(misses: number, maxMisses: number): number {
  if (misses <= 0) {
    return 0;
  }
  if (misses >= maxMisses) {
    return FIGURE_PARTS;
  }
  return Math.min(FIGURE_PARTS, Math.ceil((misses * FIGURE_PARTS) / maxMisses));
}

export const MAX_TEAMS = 6;

/** Team play: whose turn it is and each team's points. One entry (or none) means no teams. */
export interface TeamState {
  scores: number[];
  turn: number;
}

export function startTeams(count: number): TeamState {
  const size = count >= 2 ? Math.min(MAX_TEAMS, Math.round(count)) : 0;
  return { scores: Array.from({ length: size }, () => 0), turn: 0 };
}

/**
 * Update the teams after a guess. A right letter keeps the turn; a wrong one passes it. The
 * team that finds the last letter scores a point and the next word starts with the next
 * team; a lost word scores nothing and also passes the turn on.
 */
export function afterGuess(
  teams: TeamState,
  outcome: { found: number; status: RoundStatus }
): TeamState {
  const size = teams.scores.length;
  if (size < 2) {
    return teams;
  }
  const next = (teams.turn + 1) % size;
  if (outcome.status === "won") {
    return {
      scores: teams.scores.map((score, i) =>
        i === teams.turn ? score + 1 : score
      ),
      turn: next,
    };
  }
  if (outcome.found === 0 || outcome.status === "lost") {
    return { ...teams, turn: next };
  }
  return teams;
}
